import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Communication,
  DiagnosticReport,
  ServiceRequest,
  Patient,
} from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { notifications } from '@mantine/notifications';
import { HL7 } from '@consts/systems.consts';
import {
  createDiagnosticReportReviewTask,
  findReviewerTask,
  searchAllDiagnosticReportReviewTasks,
} from '../../utils/fhir-helpers';

// -----------------------------------------------------------------------------
interface UseReviewActionsParams {
  diagnosticReport: DiagnosticReport;
  serviceRequest?: ServiceRequest;
  patient?: Patient;
  onReviewersChanged?: () => void;
  onDiagnosticReportUpdated?: (report: DiagnosticReport) => void;
  onNotificationSent?: () => void;
}

// -----------------------------------------------------------------------------
export const useReviewActions = ({
  diagnosticReport,
  serviceRequest,
  patient,
  onReviewersChanged,
  onDiagnosticReportUpdated,
  onNotificationSent,
}: UseReviewActionsParams) => {
  const { t, i18n } = useTranslation('lims');
  const medplum = useMedplum();

  // ---------------------------------------------------------------------------
  const removePendingReviewer = useCallback(
    async (reviewerId: string) => {
      if (!diagnosticReport.id) {
        return;
      }
      try {
        const task = await findReviewerTask(
          medplum,
          diagnosticReport.id,
          reviewerId
        );
        if (task?.id) {
          await medplum.updateResource({ ...task, status: 'cancelled' });

          // If no active review tasks remain, revert the report to 'registered'
          const remaining = await searchAllDiagnosticReportReviewTasks(
            medplum,
            diagnosticReport.id
          );
          if (
            remaining.length === 0 &&
            diagnosticReport.status === 'preliminary'
          ) {
            const updatedDr = await medplum.updateResource({
              ...diagnosticReport,
              status: 'registered',
            });
            onDiagnosticReportUpdated?.(updatedDr);
          }

          onReviewersChanged?.();
        }
      } catch (err) {
        console.error('Error removing reviewer:', err);
      }
    },
    [medplum, diagnosticReport, onReviewersChanged, onDiagnosticReportUpdated]
  );

  // ---------------------------------------------------------------------------
  const addReviewers = useCallback(
    async (reviewerIds: string[]) => {
      if (!serviceRequest || !patient) {
        return;
      }
      try {
        for (const reviewerId of reviewerIds) {
          const reviewer = await medplum.readResource(
            'Practitioner',
            reviewerId
          );
          const task = createDiagnosticReportReviewTask(
            diagnosticReport,
            serviceRequest,
            patient,
            reviewer
          );
          await medplum.createResource(task);
        }

        // If the report is still in 'registered' state, advance it to 'preliminary'
        if (diagnosticReport.status === 'registered') {
          const updatedDr = await medplum.updateResource({
            ...diagnosticReport,
            status: 'preliminary',
          });
          onDiagnosticReportUpdated?.(updatedDr);
        }

        onReviewersChanged?.();
      } catch (err) {
        console.error('Error adding reviewers:', err);
      }
    },
    [
      diagnosticReport,
      serviceRequest,
      patient,
      medplum,
      onReviewersChanged,
      onDiagnosticReportUpdated,
    ]
  );

  // ── Notify reviewers ───────────────────────────────────────────────────────
  const [notifyModalOpen, setNotifyModalOpen] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState(
    t('reportEditor.defaultNotifyMessage')
  );
  const [notifying, setNotifying] = useState(false);

  // Reset default message when language changes
  useEffect(() => {
    setNotifyMessage(t('reportEditor.defaultNotifyMessage'));
  }, [i18n.language, t]);

  const openNotifyModal = useCallback(() => {
    setNotifyModalOpen(true);
  }, []);

  const closeNotifyModal = useCallback(() => {
    setNotifyModalOpen(false);
  }, []);

  const sendNotification = useCallback(async () => {
    if (!diagnosticReport.id) {
      return;
    }
    setNotifying(true);
    try {
      const profile = medplum.getProfile();
      const communication: Communication = {
        resourceType: 'Communication',
        status: 'completed',
        category: [
          {
            coding: [
              {
                system: HL7.TERMINOLOGY.CS.COMMUNICATION.CATEGORY.BASE,
                code: HL7.TERMINOLOGY.CS.COMMUNICATION.CATEGORY.CODES
                  .NOTIFICATION,
                display: 'Notification',
              },
            ],
          },
        ],
        basedOn: [
          { reference: `DiagnosticReport/${diagnosticReport.id}` },
        ],
        sender: profile?.id
          ? { reference: `Practitioner/${profile.id}` }
          : undefined,
        sent: new Date().toISOString(),
        payload: [{ contentString: notifyMessage }],
      };
      await medplum.createResource(communication);
      onNotificationSent?.();
      notifications.show({
        title: t('reportEditor.general.success'),
        message: t('reportEditor.notifications.reviewersNotified'),
        color: 'green',
      });
      setNotifyModalOpen(false);
      setNotifyMessage(t('reportEditor.defaultNotifyMessage'));
    } catch (err) {
      console.error('Error sending notification:', err);
    } finally {
      setNotifying(false);
    }
  }, [diagnosticReport.id, notifyMessage, medplum, onNotificationSent, t]);

  return {
    addReviewers,
    removePendingReviewer,
    notifyModalOpen,
    notifyMessage,
    notifying,
    setNotifyMessage,
    openNotifyModal,
    closeNotifyModal,
    sendNotification,
  };
};
